import { normalizeAngle } from "../game/math";
import type { SnakeIntent } from "../game/types";

export type DirectionKey = "up" | "down" | "left" | "right";
export type DirectionInputSource = "keyboard" | "pointer" | "touch";

const DIRECTION_BY_CODE: Readonly<Partial<Record<string, DirectionKey>>> = {
  ArrowUp: "up",
  KeyW: "up",
  ArrowDown: "down",
  KeyS: "down",
  ArrowLeft: "left",
  KeyA: "left",
  ArrowRight: "right",
  KeyD: "right",
};

export class DirectionKeyStack {
  private held: Array<{
    readonly direction: DirectionKey;
    readonly token: string;
  }> = [];

  press(direction: DirectionKey, token: string = direction): void {
    if (this.held.some((entry) => entry.token === token)) return;
    this.held.push({ direction, token });
  }

  release(direction: DirectionKey, token: string = direction): void {
    this.held = this.held.filter(
      (entry) => entry.token !== token || entry.direction !== direction,
    );
  }

  clear(): void {
    this.held = [];
  }

  heading(): number | null {
    const latest = (
      first: DirectionKey,
      second: DirectionKey,
    ): DirectionKey | null => {
      for (let index = this.held.length - 1; index >= 0; index -= 1) {
        const direction = this.held[index]?.direction;
        if (direction === first || direction === second) return direction;
      }
      return null;
    };
    const horizontal = latest("left", "right");
    const vertical = latest("up", "down");
    if (!horizontal && !vertical) return null;
    const x = horizontal === "left" ? -1 : horizontal === "right" ? 1 : 0;
    const y = vertical === "up" ? -1 : vertical === "down" ? 1 : 0;
    return Math.atan2(y, x);
  }
}

export class InputController {
  private heading = 0;
  private boosting = false;
  private enabled = true;
  private source: DirectionInputSource | null = null;
  private readonly directions = new DirectionKeyStack();
  private readonly suppressedCodes = new Set<string>();
  private lastPointerPosition: {
    readonly x: number;
    readonly y: number;
  } | null = null;
  private cleanup: Array<() => void> = [];

  get isEnabled(): boolean {
    return this.enabled;
  }

  attach(canvas: HTMLCanvasElement): void {
    const onPointer = (event: PointerEvent) => {
      const previousPointerPosition = this.lastPointerPosition;
      this.lastPointerPosition = { x: event.clientX, y: event.clientY };
      if (!this.enabled) return;
      if (
        event.type === "pointermove" &&
        (!previousPointerPosition ||
          Math.hypot(
            event.clientX - previousPointerPosition.x,
            event.clientY - previousPointerPosition.y,
          ) < 1)
      )
        return;
      if (
        event.pointerType === "touch" &&
        event.type === "pointermove" &&
        event.buttons === 0
      )
        return;
      const bounds = canvas.getBoundingClientRect();
      this.aim(
        Math.atan2(
          event.clientY - bounds.top - bounds.height / 2,
          event.clientX - bounds.left - bounds.width / 2,
        ),
        event.pointerType === "touch" ? "touch" : "pointer",
      );
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const direction = DIRECTION_BY_CODE[event.code];
      const boostCode =
        event.code === "Space" ||
        event.code === "ShiftLeft" ||
        event.code === "ShiftRight";
      if (!this.enabled) {
        if (direction || boostCode) {
          // Paused keys stay suppressed until release, but modal buttons must
          // retain native Space activation and dialog scrolling behavior.
          const inModal =
            event.target instanceof Element &&
            event.target.closest("dialog[open]") !== null;
          if (!inModal) event.preventDefault();
          this.suppressedCodes.add(event.code);
        }
        return;
      }
      if (this.suppressedCodes.has(event.code)) {
        event.preventDefault();
        return;
      }
      if (direction) {
        event.preventDefault();
        this.pressDirection(direction, event.code);
      }
      if (boostCode) {
        event.preventDefault();
        this.boosting = true;
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      this.suppressedCodes.delete(event.code);
      if (!this.enabled) return;
      const direction = DIRECTION_BY_CODE[event.code];
      if (direction) {
        event.preventDefault();
        this.releaseDirection(direction, event.code);
      }
      if (
        event.code === "Space" ||
        event.code === "ShiftLeft" ||
        event.code === "ShiftRight"
      ) {
        this.boosting = false;
      }
    };
    const onBlur = () => this.reset();
    canvas.addEventListener("pointermove", onPointer);
    canvas.addEventListener("pointerdown", onPointer);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    this.cleanup.push(
      () => canvas.removeEventListener("pointermove", onPointer),
      () => canvas.removeEventListener("pointerdown", onPointer),
      () => window.removeEventListener("keydown", onKeyDown),
      () => window.removeEventListener("keyup", onKeyUp),
      () => window.removeEventListener("blur", onBlur),
    );
  }

  update(deltaSeconds: number): SnakeIntent {
    void deltaSeconds;
    return {
      targetHeading: this.heading,
      boosting: this.enabled && this.boosting,
    };
  }

  pressDirection(direction: DirectionKey, token: string = direction): void {
    if (!this.enabled) return;
    if (this.source !== "keyboard") {
      this.directions.clear();
      this.source = "keyboard";
    }
    this.directions.press(direction, token);
    const heading = this.directions.heading();
    if (heading !== null) this.heading = normalizeAngle(heading);
  }

  releaseDirection(direction: DirectionKey, token: string = direction): void {
    if (!this.enabled) return;
    if (this.source !== "keyboard") return;
    this.directions.release(direction, token);
    const heading = this.directions.heading();
    if (heading !== null) this.heading = normalizeAngle(heading);
  }

  aim(
    heading: number,
    source: Exclude<DirectionInputSource, "keyboard"> = "pointer",
  ): void {
    if (!this.enabled) return;
    if (!Number.isFinite(heading)) return;
    if (this.source !== source) this.directions.clear();
    this.source = source;
    this.heading = normalizeAngle(heading);
  }

  boost(active: boolean): void {
    if (!this.enabled) return;
    this.boosting = active;
  }

  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) {
      if (!enabled) this.reset();
      return;
    }
    this.enabled = enabled;
    this.reset();
  }

  reset(): void {
    this.boosting = false;
    this.directions.clear();
    this.source = null;
  }

  dispose(): void {
    for (const cleanup of this.cleanup.splice(0)) cleanup();
    this.reset();
    this.suppressedCodes.clear();
    this.lastPointerPosition = null;
  }
}
